<template>
	<div
		id="smartmap-container"
		style="z-index: 0; position: absolute; left: 50px; right: 50px; top: 50px; bottom: 50px"
	></div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

const props = defineProps<{
	updateAddressSearchField: (val: string) => void
}>()

declare global {
	interface Window {
		webgis: any
	}
}

interface ConfigurationObject {
	OBJECTID: number
	kategorie: string
	basemap: string
	services: string
	search_service: string
	search_placeholder: string
	search_scale: string | null
}

const map = ref<any>(null)
const draggableMarker = ref<any>(null)
const identifyArray = ref<string[]>([])

const getConfiguration = async (category: string) => {
	const url = `https://service.salzburg.gv.at/arcgis/rest/services/OGD/Smartmap_Land_Salzburg/MapServer/0/query?where=kategorie=%27${category}%27&text=&objectIds=&time=&timeRelation=esriTimeRelationOverlaps&geometry=&geometryType=esriGeometryEnvelope&inSR=31258&spatialRel=esriSpatialRelIntersects&distance=&units=esriSRUnit_Foot&relationParam=&outFields=*&returnGeometry=true&returnTrueCurves=false&maxAllowableOffset=&geometryPrecision=&outSR=&havingClause=&returnIdsOnly=false&returnCountOnly=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&gdbVersion=&historicMoment=&returnDistinctValues=false&resultOffset=&resultRecordCount=&returnExtentOnly=false&sqlFormat=none&datumTransformation=&parameterValues=&rangeValues=&quantizationParameters=&featureEncoding=esriDefault&f=json`
	let attributes: ConfigurationObject | null = null
	try {
		const response = await fetch(url)
		if (!response.ok) {
			throw new Error(`Response status: ${response.status}`)
		}

		const json = await response.json()
		attributes = json.features[0].attributes
	} catch (error) {
		if (error instanceof Error) {
			console.error(error.message)
		} else {
			console.error(error)
		}
		return null
	}

	return attributes
}

interface IdentifyFeatureProperty {
	ADRCD: string
	GEMNR: string
	Gemeindename: string
	Hausnummer: string
	Postleitzahl: string
	SKZ: string
	SUBCD: string
	Straße: string
}

interface IdentifyFeatures {
	properties: IdentifyFeatureProperty
}

interface IdentifyResponse {
	features: IdentifyFeatures[]
}

const addressStore = useAppAddressStore()

const identify = async (url: string, lng: number, lat: number, ignoreStoreUpdate?: boolean) => {
	if (!url) {
		url =
			'https://www.salzburg.gv.at/api5/rest/services/orientierung/queries/adressdaten/identify?f=json&shape=POINT($COORDINATES)&shape_srs=4326&calc_srs=31258&shape_buffer=10&geometry=simple&renderFields=false'
	}
	// https://portal.salzburg.gv.at/sagismobiletest02/api5test/rest/services/orientierung/queries/adressdaten/identify?f=json&shape=POINT($COORDINATES)&shape_srs=4326&calc_srs=31258&shape_buffer=10&geometry=simple&renderFields=false
	const urlWithCoordinates = url.replace('$COORDINATES', `${lng} ${lat}`)
	console.log(urlWithCoordinates)
	try {
		map.value.setScale(400, [lng, lat])

		const response = await fetch(urlWithCoordinates)
		if (!response.ok) {
			throw new Error(`Response status: ${response.status}`)
		}

		const json = (await response.json()) as IdentifyResponse | null | undefined
		if (json && json.features && json.features.length > 0) {
			console.log(json)
			const firstFeature = json.features[0]
			if (!firstFeature) return
			const { ADRCD, Straße, Postleitzahl, Hausnummer, Gemeindename } = firstFeature.properties
			const text = `${Straße} ${Hausnummer}, ${Postleitzahl} ${Gemeindename}`

			if (!ADRCD || !lat || !lng || !text || ignoreStoreUpdate) {
				return
			}

			props.updateAddressSearchField(text)
			addressStore.$patch({
				code: ADRCD,
				latitude: lat,
				longitude: lng,
				text: text,
			})
		}
	} catch (error) {
		if (error instanceof Error) {
			console.error(error.message)
		} else {
			console.error(error)
		}
	}
}

const setDraggableMarkerPos = (lng: number, lat: number) => {
	if (!map.value) {
		return
	}
	console.log(draggableMarker.value)
	if (!draggableMarker.value) {
		draggableMarker.value = map.value.addMarker({
			lat: lat,
			lng: lng,
			icon: 'blue',
			draggable: true,
		})

		draggableMarker.value.on('dragend', function () {
			const pos = draggableMarker.value.getLatLng()

			console.log([pos.lng, pos.lat])
			map.value.setCenter([pos.lng, pos.lat])
			map.value.setScale(400, [pos.lng, pos.lat])

			identifyArray.value.forEach((url) => {
				identify(url, pos.lng, pos.lat)
			})
		})
	} else {
		draggableMarker.value.setLatLng({ lat: lat, lng: lng })
	}

	identifyArray.value.forEach((url) => {
		identify(url, lng, lat)
	})
}

onMounted(async () => {
	const hObj = 'https://www.salzburg.gv.at/sagismobile/hmac'

	if (typeof hObj === 'string') window.webgis.clientid = hObj
	else window.webgis.hmac = new window.webgis.hmacController(hObj)

	window.webgis.company = 'sagis'

	window.webgis.init(async function () {
		const configuration = await getConfiguration('EnergyApp_Adressen')
		if (!configuration) {
			console.log('No configuration object found.')
			return
		}

		const servicesConfiguration = JSON.parse(configuration.services)
		const serviceNames = servicesConfiguration.map((service: { name: string }) => service.name).join(',')
		identifyArray.value = servicesConfiguration.flatMap((item: any) =>
			item.layers.map((layer: any) => layer.identify).filter((identify: any) => identify)
		)

		window.webgis
			.$('#smartmap-container')
			.webgis_smartmap({
				map_options: {
					extent: 'sbg_m31',
					services: `orthocacheaktuell,${configuration.basemap},${serviceNames}`,
					enabled: true,
				},
				quick_search_service: configuration.search_service,
				quick_search_category: '',
				quick_search_placeholder: configuration.search_placeholder,
				quick_search_map_scale: '',
				quick_tools: 'webgis.tools.navigation.currentPos',
				on_init: function (options: any) {
					map.value = options.map
					options.map.setScale(2000000, [12.97781, 47.536102])

					servicesConfiguration.forEach((serviceCofig: any) => {
						const layerArray = serviceCofig.layers.map((layer: any) => layer.name)
						const service = options.map.getService(serviceCofig.name)
						const layerIds = service.getLayerIdsFromNames(layerArray)
						service.setServiceVisibility(layerIds, true)
					})

					options.map.events.on('click', function (_channel: any, sender: any, e: any) {
						console.log(e.lng, e.lat)
						if (e.lng && e.lat) {
							map.value.setCenter([e.lng, e.lat])
							map.value.setScale(400, [e.lng, e.lat])
							setDraggableMarkerPos(e.lng, e.lat)
						}
					})
				},
			})
			.data('eventHandlers')
			.events.on('onfeaturefound', function (channel: any, args: any) {
				const feature = args.feature
				const marker = args.marker

				map.value.removeMarker(marker)
				if (feature && feature.coords) {
					setDraggableMarkerPos(feature.coords[0], feature.coords[1])
				}
			})
	})
})

defineExpose({ identify })
</script>
